import jwtDecode from 'jwt-decode';
import * as Sentry from '@sentry/browser';

import * as APIClient from './APIClient';
import AuthError from './AuthError';
import AuthRefreshError from './AuthRefreshError';
import { isUnknownUser } from './HTTPError';

export const AUTH_KEY = 'authentication';

export const getAuth = () => {
  try {
    const value = window._localStorage.getItem(AUTH_KEY);
    return value && JSON.parse(value);
  } catch (e) {
    Sentry.withScope(scope => {
      scope.setLevel('error');
      Sentry.captureMessage(`[AuthService getAuth] error: ${e.toString()}`);
    });
    return null;
  }
};

export const setAuth = auth => window._localStorage.setItem(AUTH_KEY, JSON.stringify(auth));
export const signout = () => window._localStorage.removeItem(AUTH_KEY);

export const getAccessToken = () => {
  const auth = getAuth();
  return auth && auth.accessToken;
};

export const isTokenExpired = token => {
  try {
    const payload = jwtDecode(token);
    const epoch = Math.round(Date.now() / 1000);
    return payload.exp <= epoch;
  } catch (err) {
    Sentry.withScope(scope => {
      scope.setLevel('error');
      Sentry.captureMessage(`[AuthService isTokenExpired] error: ${err.toString()}`);
    });
    return true;
  }
};

export const signup = async params => {
  const response = await APIClient.post('/v1/auth/signup/', params);
  const result = await response.json();
  const auth = {
    accessToken: result.access,
    refreshToken: result.refresh,
  };
  setAuth(auth);
  return auth;
};

export const verifySigninToken = async token => {
  const response = await APIClient.post('/v1/auth/signin/verify/', { token });
  const payload = await response.json();
  const auth = {
    accessToken: payload.access,
    refreshToken: payload.refresh,
  };

  setAuth(auth);
};

export const refresh = async () => {
  const auth = getAuth();
  if (auth === null) {
    throw new AuthError('Unauthenticated');
  }
  if (!auth.refreshToken || isTokenExpired(auth.refreshToken)) {
    throw new AuthRefreshError('Cannot refresh token, must signin');
  }
  const params = { refresh: auth.refreshToken };
  let response = null;
  try {
    response = await APIClient.post('/v1/auth/token/refresh/', params);
  } catch (err) {
    Sentry.withScope(scope => {
      scope.setLevel('error');
      Sentry.captureMessage(`[AuthService refresh] API error: ${err.toString()}`);
    });
    throw new AuthRefreshError('Cannot refresh token, must signin');
  }
  const result = await response.json();
  auth.accessToken = result.access;
  setAuth(auth);
  return auth;
};

export const tryRefresh = async () => {
  try {
    const authTokens = getAuth();
    if (
      authTokens?.refreshToken && // Make sure the user has a refreshToken ...
      isTokenExpired(authTokens?.accessToken) // ... and that current accessToken is out-of-date
    ) {
      await refresh();
      return true; // Return true in case of refresh
    }
  } catch (error) {
    Sentry.withScope(scope => {
      scope.setLevel('error');
      Sentry.captureMessage(`[JWT Try Refresh] error: ${error.toString()}`);
    });
  }
  return false; // Return false in any other case, failure or refresh unnecessary
};

// sync check if cookie is present
// helpful to define synchronously if there is a chance use can be authenticated
export const isCookied = () => {
  const auth = getAuth();
  return Boolean(auth);
};

export const isAuthenticated = async () => {
  const auth = getAuth();
  if (auth === null) {
    return false;
  }
  if (!isTokenExpired(auth.accessToken)) {
    // Access token present and valid
    return true;
  }
  try {
    // refresh will throw if not authenticated
    await refresh();
    return true;
  } catch (err) {
    return false;
  }
};

/*
 * Request magic link to be sent by email
 *
 * params = {
 *   username: the user email (mandatory)
 *   next: the url to redirect after authentication
 *   coupon: user coupon to recover
 * }
 *
 * returns String
 *  "success" on success
 *  "unknownUser" for inexistent emails
 *  "error" on other errors
 */
export const requestMagicLink = async (params = {}) => {
  if (!params.username) {
    Sentry.captureMessage('Called requestMagicLink without username param');
    return 'error';
  }

  try {
    await APIClient.post('/v1/auth/signin/', params);
    return 'success';
  } catch (error) {
    if (isUnknownUser(error)) {
      return 'unknownUser';
    }

    Sentry.captureMessage('API Service (AuthService.requestMagicLink)');
    return 'error';
  }
};
